import axios from 'axios';
import api from '@src/api';
import cloneDeep from 'lodash/cloneDeep';

const CancelToken = axios.CancelToken;

export const state = {
  signedPostData: [],
  signedRecipePostData: null,
  uploadedCoverImageUrl: null,
  uploadedThumbnailImageUrl: null,
  uploadedCoverVideoUrl: null,
  uploadedThumbnailVideoUrl: null,
  isNewMediaAsset: false,
  tempPhotoPreview: null,
  recipeImageBlob: null,
};

export const getters = {
  hasNewMedia: (state) => (mediaType = null, modelType) => {
    return state.signedPostData.find((upload) => {
      return mediaType
        ? upload.type === mediaType && upload.requestType === modelType
        : upload.requestType === modelType;
    });
  },
};

export const mutations = {
  setSignedPostData(state, value) {
    if (value) {
      state.signedPostData.push(value);
    }
    if (!value) {
      state.signedPostData = [];
    }
  },
  setSignedPostDataUrlByIndex(state, { index, value }) {
    state.signedPostData[index].publicUrl = value;
  },
  setSignedPostDataSuccessByIndex(state, { index, value }) {
    if (state.signedPostData[index]) {
      state.signedPostData[index].success = value;
    }
  },
  setSignedPostDataCancelFuncByIndex(state, { index, value }) {
    state.signedPostData[index].cancel = value;
  },
  setSignedRecipePostData(state, value) {
    state.signedRecipePostData = value;
  },
  setUploadedCoverImageUrl(state, value) {
    state.uploadedCoverImageUrl = value;
  },
  setUploadedThumnailImageUrl(state, value) {
    state.uploadedThumbnailImageUrl = value;
  },
  setUploadedCoverVideoUrl(state, value) {
    state.uploadedCoverVideoUrl = value;
  },
  setRecipeImageBlob(state, value) {
    state.recipeImageBlob = value;
  },
  setIsNewMediaAsset(state, value) {
    state.isNewMediaAsset = value;
  },
  setClearUploadedMediaStates(state) {
    state.uploadedCoverImageUrl = null;
    state.uploadedThumbnailImageUrl = null;
    state.uploadedCoverVideoUrl = null;
    state.uploadedThumbnailVideoUrl = null;
    state.signedRecipePostData = null;
    state.signedPostData = [];
  },
  setTempPhotoPreview(state, value) {
    state.tempPhotoPreview = value;
  },
  setCurrentUploadProgress(state, { index, value }) {
    state.signedPostData[index].uploadProgress = value;
  },
  removeUploadItem(state, { index }) {
    const signedPostData = cloneDeep(state.signedPostData);
    signedPostData.splice(index, 1);
    state.signedPostData = signedPostData;
  },
};

export const actions = {
  getS3UploadUrl(
    ctx,
    { file, orgId, requestType, trainerId, type, moduleId, cohortId }
  ) {
    // moduleId and cohortId need to be provided and added as query strings when requestType is 'learn'
    const url = api.URL_GET_ORG_S3_UPLOAD_ENDPOINT.replace(':orgID', orgId)
      .replace(':requestType', requestType)
      .replace(':trainerID', trainerId)
      .concat(moduleId ? `&moduleId=${moduleId}` : '')
      .concat(cohortId ? `&cohortId=${cohortId}` : '');
    return axios
      .get(url)
      .then((res) => {
        ctx.commit('setSignedPostData', {
          ...res.data.url,
          file,
          uploadProgress: 0,
          type,
          requestType,
          success: false,
          publicUrl: null,
          cancel: null,
        });
        handleMediaType(ctx, file, orgId, res.data, type, requestType);
      })
      .catch((err) => console.warn(err));
  },
  async uploadPhoto(ctx, { model, option }) {
    const signedPostData = ctx.state.signedPostData.find(
      (upload) => model === upload.requestType && option === upload.type
    );

    if (signedPostData) {
      // We have a specific file being requested to upload
      let uploadIndex = ctx.state.signedPostData.findIndex(
        (upload) => model === upload.requestType && option === upload.type
      );
      const formData = new FormData();

      formData.append(
        'key',
        signedPostData.fields.key.concat(
          `.${signedPostData.file.type.split('/').pop()}`
        )
      );
      delete signedPostData.fields.key;
      Object.keys(signedPostData.fields).forEach((key) =>
        formData.append(key, signedPostData.fields[key])
      );
      formData.append('file', signedPostData.file);
      axios({
        method: 'POST',
        url: signedPostData.url,
        data: formData,
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          ctx.commit('setSignedPostDataCancelFuncByIndex', {
            index: uploadIndex,
            value: c,
          });
        }),
        headers: { 'Content-Type': 'multipart/form-data' },
        transformRequest: [
          (data, headers) => {
            return data;
          },
        ],
        onUploadProgress: (progressEvent) => {
          var percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          uploadIndex = ctx.state.signedPostData.findIndex(
            (upload) => model === upload.requestType && option === upload.type
          );
          ctx.commit('setCurrentUploadProgress', {
            index: uploadIndex,
            value: percentCompleted,
          });
        },
      }).then((result) => {
        if (result) {
          uploadIndex = ctx.state.signedPostData.findIndex(
            (upload) => model === upload.requestType && option === upload.type
          );
          ctx.commit('setSignedPostDataSuccessByIndex', {
            index: uploadIndex,
            value: true,
          });
          ctx.commit('removeUploadItem', { index: uploadIndex });
          if (ctx.state.signedPostData.length === 0) {
            ctx.commit('setClearUploadedMediaStates');
          }
        }
      });
    } else {
      console.warn('Invalid request type and model.');
    }
  },
  clearMedia(ctx) {
    ctx.commit('setClearUploadedMediaStates');
  },
  cancelUpload(ctx, { index }) {
    ctx.state.signedPostData[index].cancel('cancelled');
    // Remove it from the new media list
    ctx.commit('removeUploadItem', index);
  },
  // Recipe/Meal Plan Image Uploaders
  getCloudinaryUploadUrl(ctx) {
    return axios
      .get(api.URL_GET_RECIPE_IMAGE_UPLOAD_URL)
      .then((res) => {
        ctx.commit('setSignedRecipePostData', res.data.urls[0]);
      })
      .catch((err) => {
        console.log(err);
        return null;
      });
  },

  uploadToCloudinary(ctx, { blob }) {
    const fd = new FormData();
    fd.append('file', blob);

    return axios
      .post(ctx.state.signedRecipePostData, fd, {
        transformRequest: [
          (data, headers) => {
            delete headers.common.token;
            return data;
          },
        ],
      })
      .then((res) => {
        ctx.commit('setIsNewMediaAsset', false);
        ctx.commit('setClearUploadedMediaStates');
        ctx.commit('setTempPhotoPreview', null);
        return res.data;
      })
      .catch((err) => {
        console.log(err);
        return { err: 'Image could not be uploaded!' };
      });
  },
};

function handleMediaType(ctx, file, orgId, data, type, requestType) {
  const uploadIndex = ctx.state.signedPostData.findIndex(
    (upload) => requestType === upload.requestType && type === upload.type
  );
  const filePath = data.url.fields.key.concat(`.${file.type.split('/').pop()}`);

  ctx.commit('setSignedPostDataUrlByIndex', {
    index: uploadIndex,
    value: `https://org-${orgId}.s3.amazonaws.com/${filePath}`,
  });
}
